<template>
  <basic-dialog :show="show" @close="$emit('close')" maxWidth="1000">
    <template v-slot:title>
      Rates Finder
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            fab
            dark
            color="primary"
            @click="onNewSearch"
            x-small
          >
            <v-icon dark small>mdi-magnify</v-icon>
          </v-btn>
        </template>
        <span>Start New Search</span>
      </v-tooltip>
    </template>
    <v-tabs v-model="tab">
      <v-tab v-for="s of searches" :key="s.id">
        <rate-search-status :search-id="s.id" />
        {{ s.destination.name }}, {{ s.adults }} ad, {{ s.children }} ch
        <br />{{ s.startDate | formatDate }}-{{ s.endDate | formatDate }}

        <v-btn class="ml-2" icon color="primary" @click="onEditSearch(s.id)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn class="mr-2" icon color="red" @click="onRemoveSearch(s.id)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item v-for="s of searches" :key="s.id">
        <rate-search-results :search-id="s.id" @select="onServiceSelect" />
      </v-tab-item>
    </v-tabs-items>
    <rate-search-service-details
      :key="serviceDetailsWindowKey"
      :show="showRsServiceDetail"
      @close="showRsServiceDetail = false"
      v-model="selectedService"
      @add="onAddItem"
    />
    <rate-search-params-dialog
      :show="showSearchParamsDialog"
      v-model="currentSearch"
      @close="showSearchParamsDialog = false"
      @input="onSearch"
    />
    <template v-slot:actions>
      <v-btn
        color="primary"
        @click="onNewSearch"
        :disabled="creatingSearch"
        :loading="creatingSearch"
        dense
        class="ma-0"
        ><v-icon left>mdi-magnify</v-icon> New Search</v-btn
      >
    </template>
    <v-row v-if="debug">
      <v-col>
        <code>{{ currentSearch }}</code>
        <code>{{ searches }}</code>
      </v-col>
    </v-row>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../../shared/BasicDialog.vue";
import { SupplierTypeIconMixin } from "../../../../../mixins/SupplierTypeIconMixin";
import ProviderListSelect from "../../../shared/ProviderListSelect.vue";
import DestinationListSelect from "../../../shared/DestinationListSelect.vue";
import DatesRangePicker from "../../../shared/DatesRangePicker.vue";
import gql from "graphql-tag";
import RateSearchResults from "./RateSearchResults.vue";
import RateSearchStatus from "./RateSearchStatus.vue";
import RateSearchServiceDetails from "./RateSearchServiceDetails.vue";
import RateSearchParamsDialog from "./RateSearchParamsDialog.vue";

const randomize = require("randomatic");
const moment = require("moment");

const CREATE_RATES_SEARCH = gql`
  mutation createRatesSearch($input: RateSearchInput!) {
    createRateSearch(input: $input) {
      id
      destination {
        id
        name
      }
      destinationId
      agentId
      department
      providers
      serviceTypes
      adults
      children
      infants
      startDate
      endDate
      supplierName
      serviceName
      customParams
    }
  }
`;
export default {
  name: "rates-search-dialog",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    headerProps: {
      type: Object,
      default: () => null,
    },
    dayStartDate: {
      type: String,
      default: () => "",
    },
  },

  components: {
    BasicDialog,
    ProviderListSelect,
    DestinationListSelect,
    DatesRangePicker,
    RateSearchResults,
    RateSearchStatus,
    RateSearchServiceDetails,
    RateSearchParamsDialog,
  },

  mixins: [SupplierTypeIconMixin],

  data() {
    return {
      debug: false,
      menu: false,
      tab: 0,
      showSearchParamsDialog: false,
      currentSearch: null,
      searches: [],
      showRsServiceDetail: false,
      selectedService: null,
      creatingSearch: false,
      serviceDetailsWindowKey: randomize("Aa0", 10),
    };
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    searchParams() {
      return {
        startDate: this.currentSearch ? this.currentSearch.startDate : "",
        endDate: this.currentSearch ? this.currentSearch.endDate : "",
        providers: this.currentSearch
          ? this.currentSearch.providers
          : ["GTWEB"],
        serviceTypes: this.currentSearch
          ? this.currentSearch.serviceTypes
          : ["ACCOMMODATION"],
        destinationId: this.currentSearch
          ? this.currentSearch.destinationId
          : "",
        agentId: this.headerProps ? this.headerProps.agentId : "",
        department: this.headerProps ? this.headerProps.departmentName : "",
        adults: this.currentSearch ? parseInt(this.currentSearch.adults) : 2,
        children: this.currentSearch
          ? parseInt(this.currentSearch.children)
          : 0,
        infants: this.currentSearch ? parseInt(this.currentSearch.infants) : 0,
        supplierName: this.currentSearch ? this.currentSearch.supplierName : "",
        serviceName: this.currentSearch ? this.currentSearch.serviceName : "",
        customParams: this.currentSearch ? this.currentSearch.customParams : "",
      };
    },
  },

  watch: {
    show(newVal) {
      // opened from a single day always starts a fresh search for that day
      if (newVal && (this.searches.length == 0 || this.dayStartDate != "")) {
        setTimeout(() => {
          this.onNewSearch();
        }, 300);
      }
    },
  },

  methods: {
    onNewSearch() {
      this.currentSearch = {
        startDate: this.dayStartDate
          ? this.dayStartDate
          : this.headerProps
          ? this.headerProps.startDate
          : "",
        endDate: this.dayStartDate
          ? moment(this.dayStartDate).add(1, "day").toISOString(true)
          : this.headerProps
          ? this.headerProps.endDate
          : "",
        adults: this.headerProps ? this.headerProps.adults : "2",
        children: this.headerProps ? this.headerProps.children : "0",
        infants: this.headerProps ? this.headerProps.infants : "0",
        destinationId: this.headerProps ? this.headerProps.destinationId : "",
        serviceTypes: ["ACCOMMODATION"],
        providers: this.$store.getters.localSettings(
          "providerListSelectDefault",
          ["GTWEB"]
        ),
        destinationId: this.headerProps ? this.headerProps.destinationId : "",
        agentId: this.headerProps ? this.headerProps.agentId : "",
        department: this.headerProps ? this.headerProps.departmentName : "",
        supplierName: "",
        serviceName: "",
        serviceDetailsWindowKey: randomize("Aa0", 10),
      };
      this.showSearchParamsDialog = true;
    },
    onAddItem(payload) {
      console.log("adding new iti item: ", payload);
      this.showRsServiceDetail = false;
      this.$emit("add", payload);
    },
    onServiceSelect(payload) {
      this.selectedService = { ...payload };
      this.serviceDetailsWindowKey = randomize("Aa0", 10);
      this.showRsServiceDetail = true;
    },
    onEditSearch(sId) {
      const selected = this.searches.find((s) => s.id == sId);
      if (selected) {
        console.log("editing", selected);
        this.currentSearch = {
          id: selected.id,
          startDate: selected.startDate,
          endDate: selected.endDate,
          serviceTypes: [...selected.serviceTypes],
          destinationId: selected.destinationId,
          serviceName: "",
          supplierName: "",
          providers: [...selected.providers],
          includeUnpriced: false,
          adults: selected.adults,
          children: selected.children,
          infants: selected.infants,
          serviceName: selected.serviceName,
          supplierName: selected.supplierName,
          customParams: selected.customParams,
        };
      }
      this.showSearchParamsDialog = true;
    },
    onRemoveSearch(sId) {
      this.searches = this.searches.filter((s) => s.id != sId);
    },
    onDateRangeSelected(payload) {
      this.startDate = payload.dateFrom;
      this.endDate = payload.dateTo;
    },
    onSearch(payload) {
      //create new search
      this.currentSearch = { ...payload };
      this.creatingSearch = true;
      this.showSearchParamsDialog = false;
      this.$apollo
        .mutate({
          mutation: CREATE_RATES_SEARCH,
          variables: { input: { ...this.searchParams } },
        })
        .then(({ data }) => {
          //console.log("search created", data.createRateSearch.id);

          if (this.currentSearch.id && this.currentSearch.id != "") {
            const editedIndex = this.searches.findIndex(
              (s) => s.id == this.currentSearch.id
            );
            if (editedIndex > -1) {
              Object.assign(this.searches[editedIndex], {
                ...data.createRateSearch,
              });
            }
          } else {
            this.searches.push({ ...data.createRateSearch });
            this.tab = this.searches.length - 1;
          }

          this.creatingSearch = false;
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
          this.creatingSearch = false;
        });
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/builder/rates-search/RatesSearchDialog.vue